/**
 * Projects API endpoints.
 */

import type { ApiClientOptions } from "./client";
import { createApiClient } from "./client";
import type { ChatAttachmentImageRef } from "./chat";

export interface ProjectListItem {
  id: string;
  name: string;
  description: string | null;
  isStarred: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Project extends ProjectListItem {
  ownerId: string;
  graph: {
    nodes: unknown[];
    edges: unknown[];
  };
  agentSessionId: string | null;
  agentModel: string | null;
  pendingChatRunId: string | null;
  initialMessage: string | null;
}

export interface CreateProjectRequest {
  name: string;
  description?: string | null;
  message?: string;
  attachment_image?: {
    asset_id: string;
    storage_key: string;
    name: string;
    mime_type: string;
  } | null;
  agent_mode?: string;
  model?: string;
  template_id?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string | null;
  agent_model?: string | null;
  graph?: {
    nodes: unknown[];
    edges: unknown[];
  };
}

export interface ProjectListParams {
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
  starred?: boolean;
}

export interface PaginatedProjectList {
  items: ProjectListItem[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

export function createProjectsApi(options: ApiClientOptions) {
  const api = createApiClient(options);

  return {
    /**
     * List projects for the authenticated user (paginated, filtered, sorted).
     */
    list: (params: ProjectListParams = {}) => {
      const query = new URLSearchParams();
      if (params.search) query.set("search", params.search);
      if (params.sortBy) query.set("sort_by", params.sortBy);
      if (params.sortOrder) query.set("sort_order", params.sortOrder);
      if (params.limit != null) query.set("limit", String(params.limit));
      if (params.offset != null) query.set("offset", String(params.offset));
      if (params.starred != null) query.set("starred", String(params.starred));
      const qs = query.toString();
      return api.get<PaginatedProjectList>(`/api/projects${qs ? `?${qs}` : ""}`);
    },

    /**
     * Get a single project by ID.
     */
    get: (projectId: string) =>
      api.get<Project>(`/api/projects/${projectId}`),

    /**
     * Create a new project. Fires a background agent if message is provided.
     */
    create: (data: {
      name: string
      description?: string
      message?: string
      attachmentImage?: ChatAttachmentImageRef | null
      agentMode?: string
      model?: string
      templateId?: string
    }) =>
      api.post<Project>("/api/projects", {
        name: data.name,
        description: data.description,
        message: data.message,
        attachment_image: data.attachmentImage
          ? {
              asset_id: data.attachmentImage.assetId,
              storage_key: data.attachmentImage.storageKey,
              name: data.attachmentImage.name,
              mime_type: data.attachmentImage.mimeType,
            }
          : null,
        agent_mode: data.agentMode ?? "auto-detect",
        model: data.model ?? "best",
        template_id: data.templateId,
      }),

    /**
     * Update a project (partial update).
     */
    update: (projectId: string, data: UpdateProjectRequest) =>
      api.patch<Project>(`/api/projects/${projectId}`, data),

    /**
     * Delete a project.
     */
    delete: (projectId: string) =>
      api.delete<void>(`/api/projects/${projectId}`),

    /**
     * Toggle the starred status of a project.
     */
    star: (projectId: string) =>
      api.patch<Project>(`/api/projects/${projectId}/star`),

    /**
     * Clone a project with 'Copy of ' prefix.
     */
    clone: (projectId: string) =>
      api.post<Project>(`/api/projects/${projectId}/clone`),

    /**
     * List all template projects.
     */
    listTemplates: () =>
      api.get<Project[]>("/api/templates"),

    /**
     * Create a new project from a template, firing the initial_message agent task.
     */
    createFromTemplate: (templateId: string, opts: { model?: string } = {}) =>
      api.post<Project>("/api/projects", {
        name: "New Project",
        template_id: templateId,
        model: opts.model ?? "best",
      }),
  };
}
